"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Loader, Mail } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function StayConnected() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="stay-connected" ref={ref} className={`py-20 bg-background transition-opacity duration-700 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="w-full max-w-4xl mx-auto px-4 sm:px-6">
        <div className="relative rounded-xl border-2 border-secondary bg-card p-8 md:p-12 shadow-lg">
          {}
          <div className="absolute top-0 right-0 -mt-4 -mr-4 h-24 w-24 rounded-full bg-secondary/10 blur-2xl" />
          <div className="absolute bottom-0 left-0 -mb-4 -ml-4 h-24 w-24 rounded-full bg-primary/5 blur-2xl" />

          <div className="relative z-10 text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-primary mb-4">
              Stay Connected
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Receive updates on our impact and opportunities. Join our community
              of supporters.
            </p>
          </div>

          <FormProvider formId="newsletter_signup_home" sectionName="stay-connected" defaultValues={{
          first_name: "",
          last_name: "",
          email: "",
          language_preference: "English",
          consent: false
        }} validationRules={{
          email: {
            required: true,
            pattern: "email"
          },
          language_preference: {
            required: true
          },
          consent: {
            required: true
          }
        }} submitText="Subscribe" successMessage="Thank you for subscribing! We'll keep you updated." errorMessage="Something went wrong. Please try again.">
            <NewsletterForm />
          </FormProvider>

          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground/80">
              Bilingual updates available (English & Español)
            </p>
          </div>
        </div>
      </div>
    </section>;
}
function NewsletterForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-8 text-center bg-secondary/10 rounded-lg border border-secondary/20 animate-in fade-in zoom-in duration-300">
        <div className="h-12 w-12 bg-secondary text-secondary-foreground rounded-full flex items-center justify-center mb-4 shadow-sm">
          <Mail className="h-6 w-6" />
        </div>
        <h3 className="text-xl font-serif font-bold text-primary mb-2">
          Welcome to the Community!
        </h3>
        <p className="text-muted-foreground mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="border-primary/20 hover:bg-primary/5">
          Subscribe Another Email
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField control={control} name="first_name" render={({
        field
      }) => <FormItem>
              <FormLabel>First Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane" {...field} className="bg-background" />
              </FormControl>
              <FormDescription className="text-xs">Optional</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="last_name" render={({
        field
      }) => <FormItem>
              <FormLabel>Last Name</FormLabel>
              <FormControl>
                <Input placeholder="Doe" {...field} className="bg-background" />
              </FormControl>
              <FormDescription className="text-xs">Optional</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Email Address <span className="text-destructive">*</span></FormLabel>
            <FormControl>
              <Input type="email" placeholder="jane.doe@example.com" {...field} className="bg-background" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="language_preference" rules={{
      required: "Please select a language"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Preferred Language <span className="text-destructive">*</span></FormLabel>
            <FormControl>
              <select {...field} className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm shadow-xs ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50">
                <option value="English">English</option>
                <option value="Español">Español</option>
              </select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="consent" rules={{
      required: "You must agree to receive updates"
    }} render={({
      field: {
        value,
        onChange,
        ...field
      }
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4 bg-background/50">
            <FormControl>
              <input type="checkbox" checked={value} onChange={e => onChange(e.target.checked)} className="h-4 w-4 mt-1 rounded border-gray-300 text-primary focus:ring-primary accent-primary" {...field} />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="text-sm font-normal cursor-pointer">
                I agree to receive the COHCC Foundation newsletter and updates.
              </FormLabel>
              <FormDescription>
                You can unsubscribe at any time.
              </FormDescription>
              <FormMessage />
            </div>
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md text-sm text-destructive">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-bold py-6 text-lg shadow-md transition-all hover:shadow-lg">
        {isSubmitting ? <>
            <Loader className="mr-2 h-5 w-5 animate-spin" />
            Subscribing...
          </> : "Subscribe"}
      </Button>
    </Form>;
}